import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Paper,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const OrderDialogEdit = ({ open, orderId, onClose, onSave, apiUrl }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const foodStatusOptions = ['available', 'canceled', 'pending'];
  const requestStatusOptions = ['available', 'canceled', 'pending'];

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const storedToken = localStorage.getItem('accessToken');
      const response = await fetch(`${apiUrl}/orders/${orderId}`, {
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch order: ${response.status}`);
      }

      const data = await response.json();
      setOrder(data);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, orderId]);

  useEffect(() => {
    if (open && orderId) {
      fetchOrder();
    } else {
      setOrder(null);
      setError(null);
    }
  }, [open, orderId, fetchOrder]);

  const handleFoodChange = (foodIndex, field, value) => {
    setOrder((prevOrder) => {
      const updatedFoods = [...prevOrder.foods];
      updatedFoods[foodIndex] = { ...updatedFoods[foodIndex], [field]: value };
      return { ...prevOrder, foods: updatedFoods };
    });
  };

  const handleRequestChange = (foodIndex, requestIndex, field, value) => {
    setOrder((prevOrder) => {
      const updatedFoods = [...prevOrder.foods];
      const updatedRequests = [...updatedFoods[foodIndex].requests];
      updatedRequests[requestIndex] = { ...updatedRequests[requestIndex], [field]: value };
      updatedFoods[foodIndex] = { ...updatedFoods[foodIndex], requests: updatedRequests };
      return { ...prevOrder, foods: updatedFoods };
    });
  };

  const addRequest = (foodIndex) => {
    setOrder((prevOrder) => {
      const updatedFoods = [...prevOrder.foods];
      const newRequest = {
        name: '',
        addOnPrice: 0,
        status: 'available',
      };
      updatedFoods[foodIndex] = {
        ...updatedFoods[foodIndex],
        requests: [...(updatedFoods[foodIndex].requests || []), newRequest],
      };
      return { ...prevOrder, foods: updatedFoods };
    });
  };

  const removeRequest = (foodIndex, requestIndex) => {
    setOrder((prevOrder) => {
      const updatedFoods = [...prevOrder.foods];
      const updatedRequests = updatedFoods[foodIndex].requests.filter((_, idx) => idx !== requestIndex);
      updatedFoods[foodIndex] = { ...updatedFoods[foodIndex], requests: updatedRequests };
      return { ...prevOrder, foods: updatedFoods };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const storedToken = localStorage.getItem('accessToken');
      const payload = { ...order };

      const response = await fetch(`${apiUrl}/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update order');
      }

      const updatedOrder = await response.json();
      onSave(updatedOrder);
      onClose();
    } catch (err) {
      console.error('Error saving order:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Order Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2">
            Error: {error}
          </Typography>
        )}

        {order && !loading && (
          <Box>
            <Typography variant="h6">Order #{order._id.slice(-6)}</Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2">Table: {order.tableId?.tableno || 'N/A'}</Typography>
                <Typography variant="body2">Status: {order.status}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">Grand Total: RM {order.grandTotal?.toFixed(2)}</Typography>
                <Typography variant="body2">Paid: RM {order.paidAmount?.toFixed(2)}</Typography>
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>
              Food Items
            </Typography>

            {order.foods && order.foods.length > 0 ? (
              order.foods.map((food, foodIndex) => (
                <Paper key={food._id || foodIndex} sx={{ p: 2, mb: 2 }} variant="outlined">
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Title"
                        value={food.title || ''}
                        onChange={(e) => handleFoodChange(foodIndex, 'title', e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={6} sm={2}>
                      <TextField
                        label="Price"
                        type="number"
                        value={food.price || 0}
                        onChange={(e) =>
                          handleFoodChange(foodIndex, 'price', parseFloat(e.target.value) || 0)
                        }
                        fullWidth
                        size="small"
                        inputProps={{ step: 0.01 }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={2}>
                      <TextField
                        label="Quantity"
                        type="number"
                        value={food.quantity || 1}
                        onChange={(e) =>
                          handleFoodChange(foodIndex, 'quantity', parseInt(e.target.value) || 1)
                        }
                        fullWidth
                        size="small"
                        inputProps={{ min: 1 }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        label="Status"
                        value={food.status || 'available'}
                        onChange={(e) => handleFoodChange(foodIndex, 'status', e.target.value)}
                        fullWidth
                        size="small"
                      >
                        {foodStatusOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  {food.requests && food.requests.length > 0 && (
                    <Box sx={{ mt: 2, ml: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Requests
                      </Typography>

                      {food.requests.map((req, reqIndex) => (
                        <Grid container spacing={1} key={req._id || reqIndex} sx={{ mb: 1 }}>
                          <Grid item xs={5}>
                            <TextField
                              label="Name"
                              value={req.name || ''}
                              onChange={(e) =>
                                handleRequestChange(foodIndex, reqIndex, 'name', e.target.value)
                              }
                              fullWidth
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={3}>
                            <TextField
                              label="Add-on Price"
                              type="number"
                              value={req.addOnPrice || 0}
                              onChange={(e) =>
                                handleRequestChange(
                                  foodIndex,
                                  reqIndex,
                                  'addOnPrice',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              fullWidth
                              size="small"
                              inputProps={{ step: 0.01 }}
                            />
                          </Grid>

                          <Grid item xs={3}>
                            <TextField
                              select
                              label="Status"
                              value={req.status || 'available'}
                              onChange={(e) =>
                                handleRequestChange(foodIndex, reqIndex, 'status', e.target.value)
                              }
                              fullWidth
                              size="small"
                            >
                              {requestStatusOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={1}>
                            <IconButton
                              size="small"
                              onClick={() => removeRequest(foodIndex, reqIndex)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                    </Box>
                  )}

                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addRequest(foodIndex)}
                    >
                      Add Request
                    </Button>
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography variant="body2">No food items in this order.</Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || !order}
        >
          {saving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDialogEdit;